import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Send, Building2, ChevronRight } from 'lucide-react';
import contactImg from '../assets/contact.png';
import SEO from './SEO';
import contactImg2 from '../assets/contact1.png';

const ContactUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    mobile: '',
    services: [],
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const servicesRef = useRef(null);

  const gradientBg = "bg-gradient-to-r from-[#0EA5E9] to-[#0B2B5E]";

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  // Handle click outside to close services dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const servicesList = [
    "Website Development",
    "Digital Marketing",
    "SEO & SEM",
    "Google Ads & Meta Ads Management",
    "Social Media Marketing",
    "Mobile App Development",
    "Custom Software Development",
    "Graphic Designing",
    "Video Editing & Content Creation"
  ];

  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const isSelected = prev.services.includes(service);
      const updated = isSelected
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];
      // validate services on change
      setErrors(prevErr => ({
        ...prevErr,
        services: updated.length === 0 ? 'Please select at least one service.' : ''
      }));
      return { ...prev, services: updated };
    });
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return value.trim() === '' ? 'First name is required.' : '';
      case 'email':
        if (value.trim() === '') return 'Email address is required.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
        return '';
      case 'mobile':
        if (value.trim() === '') return 'Mobile number is required.';
        if (!/^[0-9]{10}$/.test(value.trim())) return 'Enter a valid 10-digit mobile number.';
        return '';
      case 'message':
        return value.trim() === '' ? 'Message is required.' : '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleWhatsAppSubmit = (e) => {
    if (e) e.preventDefault();
    // Mark all fields as touched
    const allTouched = { firstName: true, email: true, mobile: true, message: true, services: true };
    setTouched(allTouched);
    // Validate all fields
    const newErrors = {
      firstName: validateField('firstName', formData.firstName),
      email: validateField('email', formData.email),
      mobile: validateField('mobile', formData.mobile),
      message: validateField('message', formData.message),
      services: formData.services.length === 0 ? 'Please select at least one service.' : ''
    };
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(err => err !== '');
    if (hasErrors) return;
    const { firstName, lastName, email, countryCode, mobile, services, message } = formData;
    const servicesText = services.join(", ");
    const text = `Hello VishwaDigitech,\n\nNew contact form submission:\n*Name*: ${firstName} ${lastName}\n*Email*: ${email}\n*Mobile*: ${countryCode} ${mobile}\n*Services*: ${servicesText}\n*Message*: ${message}`;
    const encodedText = encodeURIComponent(text);
    const whatsappUrl = `https://wa.me/919699938509?text=${encodedText}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#060A14] text-white pt-24 pb-20" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Contact Us – Vishwa DigiTech Sangli"
        description="Get in touch with Vishwa DigiTech. Contact our team in Sangli for website development, digital marketing, app development & more. Free consultation available. Call or WhatsApp us today."
        keywords="contact vishwa digitech, digital marketing company sangli contact, web development sangli contact, IT company sangli phone, free consultation digital marketing"
        canonicalPath="/contact"
      />

      {/* Banner Image (Matches Blog Layout) */}
      <div className={`relative w-full overflow-hidden mb-24 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} border-b border-white/10`}>
        <img src={contactImg} alt="Contact Us" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060A14] via-[#060A14]/80 to-transparent z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="py-24 md:py-36 lg:py-48 max-w-3xl">
            <div className={`transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="inline-block px-5 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-6 backdrop-blur-md shadow-lg">
                Get In Touch
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-[#0EA5E9] mb-6 tracking-tight">
                CONTACT US
              </h1>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed font-light">
                We'd love to show you how our digital solutions can help your business scale. <br className="hidden md:block" />
                Fill out the form and a strategic advisor will be in touch within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Massive Split Card Container */}
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Massive Split Card */}
        <div className={`max-w-7xl mx-auto bg-[#0A0F1F] rounded-3xl overflow-hidden shadow-2xl border border-white/5 flex flex-col lg:flex-row transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>

          {/* Left Side: Professional Form */}
          <div className="w-full lg:w-3/5 p-8 md:p-12 lg:p-16 relative flex flex-col justify-center">
            <form className="space-y-6" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* First Name */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-300">First Name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter Your Name"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    onBlur={() => handleBlur('firstName')}
                    className={`w-full bg-[#1E293B] border rounded-xl px-4 py-3 text-white focus:outline-none transition-colors ${
                      touched.firstName && errors.firstName
                        ? 'border-red-500 focus:border-red-400'
                        : touched.firstName && !errors.firstName
                        ? 'border-green-500 focus:border-green-400'
                        : 'border-white/10 focus:border-[#0EA5E9]'
                    }`}
                  />
                  {touched.firstName && errors.firstName && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.firstName}
                    </p>
                  )}
                </div>
                {/* Last Name */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-300">Last Name</label>
                  <input
                    type="text"
                    placeholder="Enter Your Last Name"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className="w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#0EA5E9] transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Email */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-300">Email Address <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    placeholder="Enter Your Email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    className={`w-full bg-[#1E293B] border rounded-xl px-4 py-3 text-white focus:outline-none transition-colors ${
                      touched.email && errors.email
                        ? 'border-red-500 focus:border-red-400'
                        : touched.email && !errors.email
                        ? 'border-green-500 focus:border-green-400'
                        : 'border-white/10 focus:border-[#0EA5E9]'
                    }`}
                  />
                  {touched.email && errors.email && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.email}
                    </p>
                  )}
                </div>
                {/* Mobile */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-300">Mobile Number <span className="text-red-400">*</span></label>
                  <div className={`flex bg-[#1E293B] border rounded-xl overflow-hidden transition-colors ${
                    touched.mobile && errors.mobile
                      ? 'border-red-500'
                      : touched.mobile && !errors.mobile
                      ? 'border-green-500'
                      : 'border-white/10 focus-within:border-[#0EA5E9]'
                  }`}>
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="bg-transparent text-sm text-gray-300 border-none outline-none pl-2 pr-1 py-3 cursor-pointer w-[90px]"
                    >
                      <option value="+91" className="bg-[#0F1629] text-white">IN (+91)</option>
                      <option value="+1" className="bg-[#0F1629] text-white">US (+1)</option>
                      <option value="+44" className="bg-[#0F1629] text-white">UK (+44)</option>
                      <option value="+971" className="bg-[#0F1629] text-white">UAE (+971)</option>
                      <option value="+61" className="bg-[#0F1629] text-white">AU (+61)</option>
                    </select>
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      value={formData.mobile}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange('mobile', val);
                      }}
                      onBlur={() => handleBlur('mobile')}
                      maxLength="10"
                      className="w-full bg-transparent border-none outline-none px-4 py-3 text-white"
                    />
                  </div>
                  {touched.mobile && errors.mobile && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.mobile}
                    </p>
                  )}
                </div>
              </div>
              {/* Services */}
              <div className="space-y-1 relative" ref={servicesRef}>
                <label className="text-sm font-medium text-gray-300">Our Services <span className="text-red-400">*</span></label>
                <div
                  className={`w-full bg-[#1E293B] border rounded-xl px-4 py-3 text-white cursor-pointer flex justify-between items-center transition-colors hover:border-white/20 ${
                    touched.services && errors.services
                      ? 'border-red-500'
                      : touched.services && !errors.services
                      ? 'border-green-500'
                      : 'border-white/10'
                  }`}
                  onClick={() => { setIsServicesOpen(!isServicesOpen); setTouched(prev => ({ ...prev, services: true })); }}
                >
                  <span className={formData.services.length === 0 ? "text-gray-400" : "text-white"}>
                    {formData.services.length === 0 ? "Select Services" : `${formData.services.length} service${formData.services.length > 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isServicesOpen ? 'rotate-90 text-[#0EA5E9]' : 'text-gray-400'}`} />
                </div>
                {touched.services && errors.services && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.services}
                  </p>
                )}
                {isServicesOpen && (
                  <div className="absolute z-50 w-full mt-2 bg-[#0F1629] border border-white/10 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] max-h-60 overflow-y-auto">
                    <div className="p-2 space-y-1">
                      {servicesList.map((service, idx) => (
                        <label key={idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-500 text-[#0EA5E9] focus:ring-[#0EA5E9] bg-[#1E293B] cursor-pointer"
                            checked={formData.services.includes(service)}
                            onChange={() => handleServiceToggle(service)}
                          />
                          <span className="text-sm text-gray-300">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Message */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-300">Message <span className="text-red-400">*</span></label>
                <textarea
                  rows="4"
                  placeholder="How can we help you?"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  onBlur={() => handleBlur('message')}
                  className={`w-full bg-[#1E293B] border rounded-xl px-4 py-3 text-white focus:outline-none transition-colors resize-none ${
                    touched.message && errors.message
                      ? 'border-red-500 focus:border-red-400'
                      : touched.message && !errors.message
                      ? 'border-green-500 focus:border-green-400'
                      : 'border-white/10 focus:border-[#0EA5E9]'
                  }`}
                />
                {touched.message && errors.message && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.message}
                  </p>
                )}
              </div>
              <button type="button" onClick={handleWhatsAppSubmit} className={`w-full ${gradientBg} text-white px-8 py-4 rounded-xl font-bold tracking-wide flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-lg`}>
                Send Message <Send className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Right Side: Image & Info Panel */}
          <div className="w-full lg:w-2/5 relative min-h-[400px] lg:min-h-full bg-[#0F1629]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#060A14] to-transparent z-10 opacity-60" />
            <img
              src={contactImg2}
              alt="Contact VishwaDigitech"
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Contact Details Glass Overlay */}
            <div className="absolute inset-x-0 bottom-0 px-10 lg:px-20 pb-8 md:pb-12 pt-10 z-20">
              <div className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl p-5 md:p-6 shadow-[0_8px_32px_0_rgba(0,0,0,0.5)]">
                <h3 className="text-xl font-bold text-white text-center mb-5 drop-shadow-md">Our Headquarters</h3>

                <div className="flex flex-col space-y-3 w-full mt-2">
                  {/* Top Row: Location & Phone side by side */}
                  <div className="flex flex-row items-center justify-center w-full gap-6 sm:gap-10">
                    {/* Location */}
                    <a href="https://www.google.com/maps/search/?api=1&query=Vishwa+DigiTech,+Sangli,+Maharashtra" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:opacity-80 transition-opacity cursor-pointer group">
                      <MapPin className="w-5 h-5 text-white shrink-0 group-hover:scale-110 transition-transform drop-shadow-sm" />
                      <div className="text-left">
                        <p className="text-white font-medium group-hover:text-gray-200 transition-colors drop-shadow-sm text-sm">VishwaDigitech</p>
                        <p className="text-gray-200 text-xs mt-0.5 drop-shadow-sm">Sangli, Maharashtra</p>
                      </div>
                    </a>

                    {/* Phone */}
                    <a href="tel:+919699938509" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer group">
                      <Phone className="w-5 h-5 text-white shrink-0 group-hover:scale-110 transition-transform drop-shadow-sm" />
                      <p className="text-gray-200 text-sm font-medium group-hover:text-white transition-colors drop-shadow-sm">+91 96999 38509</p>
                    </a>
                  </div>

                  {/* Bottom Row: Email (Center aligned) */}
                  <div className="w-full pt-1">
                    <a href="mailto:info@vishwadigitech.com" className="flex items-center justify-center gap-3 hover:opacity-80 transition-opacity cursor-pointer group w-full">
                      <Mail className="w-4 h-4 text-white shrink-0 group-hover:scale-110 transition-transform drop-shadow-sm" />
                      <p className="text-gray-200 text-sm font-medium group-hover:text-white transition-colors drop-shadow-sm">info@vishwadigitech.com</p>
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Interactive Map Section */}
      <div className={`w-full mt-24 transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <div className="inline-block px-5 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-4 shadow-lg backdrop-blur-md">
            Find Us
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#0B2B5E]">Global Reach</span>
          </h2>
        </div>

        <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden border-y border-white/5 group">
          {/* Animated Glow Border Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0EA5E9]/10 to-[#0B2B5E]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10 pointer-events-none" />

          {/* Google Maps Embed */}
          <iframe
            src="https://maps.google.com/maps?q=Vishwa+DigiTech,+Sangli,+Maharashtra&t=&z=15&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="VishwaDigitech Location"
            className="relative z-0"
          ></iframe>

          {/* Glassmorphism Location Card Overlay */}
          <div className="absolute bottom-4 left-4 right-4 md:right-auto md:bottom-12 md:left-12 z-20 bg-[#0A0F1F]/90 backdrop-blur-xl border border-white/10 p-5 md:p-6 rounded-2xl shadow-2xl md:max-w-sm transform transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_15px_40px_-10px_rgba(14,165,233,0.4)]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center border border-[#0EA5E9]/30">
                <MapPin className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">VishwaDigitech HQ</h3>
                <p className="text-xs text-[#0EA5E9] font-semibold tracking-wider uppercase">Main Office</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Drop by our office in sangli to discuss how we can elevate your digital presence over a cup of coffee.
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Vishwa+DigiTech,+Sangli,+Maharashtra"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20 border border-[#0EA5E9]/30 text-[#0EA5E9] hover:text-white font-bold text-sm tracking-widest uppercase py-3 rounded-xl transition-all duration-300 group/btn"
            >
              Open in Maps
              <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ContactUs;
